import { useMutation } from '@apollo/client';
import { IconButton, Text, TextField } from '@radix-ui/themes';
import { Button } from '@hubertai/hubert-component-library';
import { Eye, EyeOff } from 'lucide-react';
import { Form } from 'radix-ui';
import { type FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CHANGE_PASSWORD } from '../../gql';
import { getPasswordValidationMessage, revalidateFields } from '../../utils/security';

type SubmitData = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const ChangePassword = () => {
  const { t } = useTranslation();
  const [changePassword, { loading: changePasswordLoading }] = useMutation(CHANGE_PASSWORD);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [invalidNewPasswordMessage, setInvalidNewPasswordMessage] = useState(
    t('settings.security.validation.invalidPasswordFallback'),
  );

  const [serverErrors, setServerErrors] = useState<{ password: boolean }>({
    password: false,
  });
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();

    const form = formEvent.currentTarget;

    revalidateFields([newPasswordRef.current, confirmPasswordRef.current]);

    if (!form.checkValidity()) {
      return;
    }

    const formData = Object.fromEntries(new FormData(form)) as unknown as SubmitData;
    const oldPassword = formData.oldPassword.trim();
    const newPassword = formData.newPassword.trim();
    const confirmPassword = formData.confirmPassword.trim();

    changePassword({
      variables: {
        newPassword: newPassword,
        newPasswordRepeated: confirmPassword,
        oldPassword: oldPassword,
      },
    })
      .then((response) => {
        if (response.data.changePassword.__typename === 'ChangePasswordInvalidInputError') {
          setServerErrors({ password: true });
        } else if (response.data.changePassword.__typename === 'ChangePasswordPayload') {
          toast.success(t('settings.security.toast.success'));
          form.reset();
        }
      })
      .catch(() => {
        toast.error(t('settings.security.toast.error'));
        form.reset();
      });
  };

  return (
    <div className="border border-gray-200 rounded p-4">
      <h2 className="truncate pb-4">{t('settings.security.heading')}</h2>
      <Form.Root
        className="w-1/2"
        onSubmit={handleSubmit}
        onClearServerErrors={() => setServerErrors({ password: false })}
      >
        <Form.Field name="oldPassword" className="mb-4" serverInvalid={serverErrors.password}>
          <div className="flex items-baseline justify-between pr-12">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('settings.security.form.currentPasswordLabel')}</strong>
              </Text>
            </Form.Label>
            <Text size="1" color="red">
              <Form.Message match="valueMissing">{t('settings.security.form.currentPasswordRequired')}</Form.Message>
              {serverErrors.password && <Form.Message>{t('settings.security.form.wrongPassword')}</Form.Message>}
            </Text>
          </div>
          <div className="flex flex-row gap-4 items-center">
            <Form.Control asChild>
              <TextField.Root
                placeholder={t('settings.security.form.currentPasswordPlaceholder')}
                type={showOldPassword ? 'text' : 'password'}
                required
                className="w-full"
                size="3"
                onChange={() => setServerErrors((prev) => ({ ...prev, password: false }))}
              />
            </Form.Control>
            <IconButton
              type="button"
              size="3"
              variant="ghost"
              color="gray"
              highContrast
              className="m-0"
              onClick={() => setShowOldPassword(!showOldPassword)}
            >
              {showOldPassword ? <Eye /> : <EyeOff />}
            </IconButton>
          </div>
        </Form.Field>
        <Form.Field name="newPassword" className="mb-4">
          <div className="flex items-baseline justify-between pr-12">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('settings.security.form.newPasswordLabel')}</strong>
              </Text>
            </Form.Label>
            <Text size="1" color="red">
              <Form.Message match="valueMissing">{t('settings.security.form.newPasswordRequired')}</Form.Message>
              <Form.Message
                match={(value) => {
                  const message = getPasswordValidationMessage(
                    value,
                    t,
                    'settings.security.validation.invalidPasswordFallback',
                  );
                  if (message) {
                    setInvalidNewPasswordMessage(message);
                    return true;
                  }
                  return false;
                }}
              >
                {invalidNewPasswordMessage}
              </Form.Message>
            </Text>
          </div>
          <div className="flex flex-row gap-4 items-center">
            <Form.Control asChild>
              <TextField.Root
                ref={newPasswordRef}
                placeholder={t('settings.security.form.newPasswordPlaceholder')}
                type={showPassword ? 'text' : 'password'}
                required
                className="w-full"
                size="3"
              />
            </Form.Control>
            <IconButton
              type="button"
              size="3"
              variant="ghost"
              color="gray"
              highContrast
              className="m-0"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <Eye /> : <EyeOff />}
            </IconButton>
          </div>
        </Form.Field>
        <Form.Field name="confirmPassword" className="mb-4">
          <div className="flex items-baseline justify-between pr-12">
            <Form.Label asChild>
              <Text size="2">
                <strong>{t('settings.security.form.confirmPasswordLabel')}</strong>
              </Text>
            </Form.Label>
            <Text size="1" color="red">
              <Form.Message match="valueMissing">{t('settings.security.form.confirmPasswordRequired')}</Form.Message>
              <Form.Message match={(value, formData) => value !== (formData.get('newPassword') as string)}>
                {t('settings.security.form.passwordsMustMatch')}
              </Form.Message>
            </Text>
          </div>
          <div className="flex flex-row gap-4 items-center">
            <Form.Control asChild>
              <TextField.Root
                ref={confirmPasswordRef}
                placeholder={t('settings.security.form.confirmPasswordPlaceholder')}
                type={showPasswordConfirmation ? 'text' : 'password'}
                required
                className="w-full"
                size="3"
              />
            </Form.Control>
            <IconButton
              type="button"
              size="3"
              variant="ghost"
              color="gray"
              highContrast
              className="m-0"
              onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
            >
              {showPasswordConfirmation ? <Eye /> : <EyeOff />}
            </IconButton>
          </div>
        </Form.Field>
        <Form.Submit asChild>
          <Button
            size="3"
            color="grass"
            variant="solid"
            className="w-1/2"
            loading={changePasswordLoading}
            disabled={changePasswordLoading}
          >
            {t('settings.security.updateButton')}
          </Button>
        </Form.Submit>
      </Form.Root>
    </div>
  );
};

export default ChangePassword;
